import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, deleteDoc, doc } from "firebase/firestore";
import { FaUserEdit, FaSort, FaSortUp, FaSortDown, FaTrash } from "react-icons/fa";
import Navbar from "../components/AgentNavbar";
import ManageAgentForm from "./ManageAgentForm"; // ✅ Import side panel

export default function ManageAgents() {
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [selectedAgent, setSelectedAgent] = useState(null); // ✅ New state

  useEffect(() => {
    const unsubscribeAgents = onSnapshot(collection(db, "agents"), (snapshot) => {
      const agentList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAgents(agentList);
    });

    return () => unsubscribeAgents();
  }, []);

  useEffect(() => {
    let tempAgents = [...agents];

    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase();
      tempAgents = tempAgents.filter((a) =>
        [a.id, a.username, a.password, a.role]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(query))
      );
    }

    if (sortConfig.key) {
      const dir = sortConfig.direction === "asc" ? 1 : -1;
      tempAgents.sort((a, b) => {
        const aValue = a[sortConfig.key] ?? "";
        const bValue = b[sortConfig.key] ?? "";

        if (typeof aValue === "string" || typeof bValue === "string") {
          return dir === 1
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        } else {
          return 0;
        }
      });
    }

    setFilteredAgents(tempAgents);
  }, [agents, searchQuery, sortConfig]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort style={{ marginLeft: "4px" }} />;
    return sortConfig.direction === "asc" ? (
      <FaSortUp style={{ marginLeft: "4px" }} />
    ) : (
      <FaSortDown style={{ marginLeft: "4px" }} />
    );
  };

  const handleDelete = async (agentId) => {
    const confirmed = window.confirm("Are you sure you want to delete this agent?");
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, "agents", agentId));
    } catch (err) {
      console.error("Error deleting agent:", err);
      alert("Failed to delete agent.");
    }
  };

  return (
    <div style={{ backgroundColor: "#f9fafb", minHeight: "100vh" }}>
      <Navbar />
      <div
        style={{
          width: "95%",
          maxWidth: "1000px",
          margin: "2rem auto",
          padding: "2rem",
          background: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
          fontFamily: "sans-serif",
          position: "relative",
        }}
      >
        <h2 style={{ fontSize: "1.5rem", fontWeight: 600, marginBottom: "0.5rem" }}>
          Manage Agents
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "1.5rem", fontSize: "0.9rem" }}>
          View, search, and manage system agents.
        </p>

        <input
          type="text"
          placeholder="Search agents..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: "100%",
            padding: "0.6rem 1rem",
            marginBottom: "1.5rem",
            borderRadius: "8px",
            border: "1px solid #d1d5db",
            fontSize: "0.9rem",
          }}
        />

        <div style={{ overflowX: "auto", width: "100%" }}>
          <table
            style={{
              width: "100%",
              minWidth: "600px",
              borderCollapse: "collapse",
              fontSize: "0.85rem",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f3f4f6", textAlign: "left" }}>
                <th style={thStyle} onClick={() => requestSort("id")}>
                  Agent ID {getSortIcon("id")}
                </th>
                <th style={thStyle} onClick={() => requestSort("username")}>
                  Username {getSortIcon("username")}
                </th>
                <th style={thStyle} onClick={() => requestSort("password")}>
                  Password {getSortIcon("password")}
                </th>
                <th style={thStyle} onClick={() => requestSort("role")}>
                  Role {getSortIcon("role")}
                </th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAgents.map((agent) => (
                <tr
                  key={agent.id}
                  style={{
                    borderBottom: "1px solid #e5e7eb",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f9fafb")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <td style={tdStyle}>{agent.id}</td>
                  <td style={tdStyle}>{agent.username}</td>
                  <td style={tdStyle}>{agent.password}</td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        padding: "0.25rem 0.75rem",
                        borderRadius: "999px",
                        backgroundColor: "#1e40af",
                        color: "white",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                      }}
                    >
                      {agent.role}
                    </span>
                  </td>
                  <td style={{ display: "flex", gap: "0.5rem" }}>
                    <button
                      onClick={() => setSelectedAgent(agent)} // ✅ open panel
                      style={editBtnStyle}
                      title="Edit Agent"
                    >
                      <FaUserEdit />
                    </button>
                    <button
                      onClick={() => handleDelete(agent.id)}
                      style={deleteBtnStyle}
                      title="Delete Agent"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ✅ Side Panel for editing */}
        {selectedAgent && (
          <ManageAgentForm
            agent={selectedAgent}
            onClose={() => setSelectedAgent(null)}
            onAgentUpdated={(updated) => {
              setAgents((prev) =>
                prev.map((a) => (a.id === updated.id ? updated : a))
              );
              setSelectedAgent(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

const thStyle = {
  padding: "0.75rem",
  fontSize: "0.8rem",
  color: "#374151",
  fontWeight: 600,
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap",
  cursor: "pointer",
};

const tdStyle = {
  padding: "0.25rem",
  fontSize: "0.8rem",
  color: "#374151",
  verticalAlign: "middle",
  maxWidth: "220px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "normal",
};

const editBtnStyle = {
  cursor: "pointer",
  backgroundColor: "#e0e7ff",
  border: "none",
  borderRadius: "6px",
  padding: "0.4rem 0.6rem",
  color: "#1e40af",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const deleteBtnStyle = {
  cursor: "pointer",
  backgroundColor: "#fee2e2",
  border: "none",
  borderRadius: "6px",
  padding: "0.4rem 0.6rem",
  color: "#991b1b",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
